'use client'

import { useAllLoadTypes } from '@repo/sdk'
import { parseAsIsoDate, parseAsString, useQueryStates } from 'nuqs'

export interface DateRange {
  from: Date
  to: Date
}

export const useInquiriesFilter = () => {
  const [filterQuery, setFilterQuery] = useQueryStates(
    {
      cargoType: parseAsString,
      companyId: parseAsString,
      criterium: parseAsString.withDefault('created_at'),
      dateFrom: parseAsIsoDate,
      dateTo: parseAsIsoDate,
      originLocation: parseAsString,
    },
    { history: 'push' },
  )

  const parseDateFilter = (type: 'created_at' | 'load_date' | 'unload_date') => {
    return `&filter[${type}]=${dateFrom?.toISOString().split('T')[0]},${dateTo?.toISOString().split('T')[0]}`
  }

  const { cargoType, companyId, criterium, dateFrom, dateTo, originLocation } = filterQuery
  const { data: loadTypes } = useAllLoadTypes()

  const companyIdQuery = companyId ? `&filter[company_id]=${companyId}` : ''
  const collectionDateQuery = parseDateFilter('load_date')
  const deliveryDateQuery = parseDateFilter('unload_date')
  const createdAtDateQuery = parseDateFilter('created_at')
  const cargoTypeQuery = cargoType ? `&filter[load_type]=${cargoType}` : ''

  const dynamicQuery =
    companyIdQuery +
    (dateFrom || dateTo
      ? criterium === 'collection'
        ? collectionDateQuery
        : criterium === 'delivery'
          ? deliveryDateQuery
          : createdAtDateQuery
      : '') +
    cargoTypeQuery

  const setDateRange = async (range: any | undefined) => {
    if (!range) return

    const adjustedRange = {
      dateFrom: range.from ? new Date(range.from.setHours(12, 0, 0, 0)) : undefined,
      dateTo: range.to ? new Date(range.to.setHours(12, 0, 0, 0)) : undefined,
    }

    await setFilterQuery(prevValue => ({ ...prevValue, ...adjustedRange }))
  }

  const resetFilters = () => {
    setFilterQuery({
      cargoType: null,
      companyId: null,
      criterium: 'created_at',
      dateFrom: null,
      dateTo: null,
    })
  }

  const setCriterium = (value: string) => {
    setFilterQuery(prevValue => ({ ...prevValue, criterium: value }))
  }

  const setCompanyId = (value: string) => {
    setFilterQuery(prevValue => ({ ...prevValue, companyId: value }))
  }

  const setCargoType = (value: string) => {
    setFilterQuery(prevValue => ({ ...prevValue, cargoType: value }))
  }

  const setOriginLocation = (value: string) => {
    setFilterQuery(prevValue => ({ ...prevValue, originLocation: value }))
  }

  return {
    cargoType,
    companyId,
    criterium,
    dateRange: { from: dateFrom, to: dateTo },
    dynamicQuery,
    loadTypes,
    originLocation,
    resetFilters,
    setCargoType,
    setCompanyId,
    setCriterium,
    setDateRange,
    setOriginLocation,
  }
}
